import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert,
    Stack, Divider, Table, TableHead, TableBody, TableRow, TableCell,
    FormControl, Select, MenuItem
} from '@mui/material';
import * as api from './orderTypesApi';
import type { OrderType, Language, PrintDocumentTemplate, DeliveryPlanningTemplate } from './orderTypesApi';
import { sx14, paperSx, FormRow, DetailHeader, mergeLocalizations } from '../common/detailView';

interface Props {
    orderTypeId: number | null;
}

export function OrderTypeView({ orderTypeId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<OrderType | null>(null);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [templates, setTemplates] = useState<PrintDocumentTemplate[]>([]);
    const [dpTemplates, setDpTemplates] = useState<DeliveryPlanningTemplate[]>([]);
    const [dpEnabled, setDpEnabled] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<OrderType | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const buildDefaultLoc = (lang: Language) => ({ language: lang });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [langs, tpls, dpResp, enabled] = await Promise.all([
                api.getLanguages(),
                api.getPrintDocumentTemplates(),
                api.getDeliveryPlanningTemplates(),
                api.isDeliveryPlanningEnabled(),
            ]);
            setLanguages(langs);
            setTemplates(tpls);
            setDpTemplates(dpResp?.deliveryPlanningTemplates ?? []);
            setDpEnabled(enabled === true);

            if (orderTypeId === null) {
                setItem({
                    id: 0,
                    orderTypeName: '',
                    printDocumentTemplate: null,
                    pickingListDocumentTemplate: null,
                    deliveryPlanningTemplate: null,
                    localizations: mergeLocalizations([], langs, buildDefaultLoc)
                });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getOrderType(orderTypeId);
                data.localizations = mergeLocalizations(data.localizations ?? [], langs, buildDefaultLoc);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [orderTypeId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const payload: OrderType = { ...item };
            if (dpEnabled) {
                const dpId = payload.deliveryPlanningTemplate?.id ?? 0;
                payload.deliveryPlanningTemplateId = dpId > 0 ? dpId : null;
            } else {
                payload.deliveryPlanningTemplateId = null;
            }
            const response = isNew ? await api.createOrderType(payload) : await api.updateOrderType(payload);
            response.localizations = mergeLocalizations(response.localizations ?? [], languages, buildDefaultLoc);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof OrderType>(field: K, value: OrderType[K]) => {
        setItem({ ...item, [field]: value });
    };

    const updateLocalization = (languageId: number, name: string) => {
        update('localizations', item.localizations.map(loc =>
            loc.language.id === languageId ? { ...loc, name } : loc
        ));
    };

    const renderTemplateSelect = (
        value: PrintDocumentTemplate | null,
        onSelect: (tpl: PrintDocumentTemplate | null) => void
    ) => (
        <FormControl size="small" fullWidth>
            <Select
                value={value?.id ?? ''}
                onChange={e => {
                    const tpl = templates.find(x => x.id === e.target.value);
                    onSelect(tpl ?? null);
                }}
            >
                {[...templates]
                    .sort((a, b) => a.id - b.id)
                    .map(tpl => (
                        <MenuItem key={tpl.id} value={tpl.id}>{tpl.description}</MenuItem>
                    ))}
            </Select>
        </FormControl>
    );

    const sortedLocs = [...item.localizations].sort((a, b) =>
        (a.language.name ?? '').localeCompare(b.language.name ?? '')
    );

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={item.orderTypeName ?? ''}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('ordertype.orderTypeName')}>
                        <Typography sx={sx14}>{item.orderTypeName ? t(item.orderTypeName) : ''}</Typography>
                    </FormRow>

                    <FormRow label={t('ordertype.printDocumentTemplate')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.printDocumentTemplate?.description}</Typography>
                        ) : (
                            renderTemplateSelect(item.printDocumentTemplate, v => update('printDocumentTemplate', v))
                        )}
                    </FormRow>

                    <FormRow label={t('ordertype.pickingListDocumentTemplate')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.pickingListDocumentTemplate?.description}</Typography>
                        ) : (
                            renderTemplateSelect(item.pickingListDocumentTemplate, v => update('pickingListDocumentTemplate', v))
                        )}
                    </FormRow>

                    {dpEnabled && (
                        <FormRow label={t('ordertype.deliveryPlanningTemplate')}>
                            {!isEditing ? (
                                <Typography sx={sx14}>{item.deliveryPlanningTemplate?.deliveryPlanningTemplateName}</Typography>
                            ) : (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={item.deliveryPlanningTemplate?.id ?? ''}
                                        onChange={e => {
                                            const dpt = dpTemplates.find(x => x.id === e.target.value);
                                            update('deliveryPlanningTemplate', dpt ?? null);
                                        }}
                                    >
                                        {[...dpTemplates]
                                            .sort((a, b) => a.deliveryPlanningTemplateName.localeCompare(b.deliveryPlanningTemplateName))
                                            .map(dpt => (
                                                <MenuItem key={dpt.id} value={dpt.id}>{dpt.deliveryPlanningTemplateName}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            )}
                        </FormRow>
                    )}

                    <Divider />

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold' }}>{t('common.language')}</TableCell>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold' }}>{t('ordertype.orderTypeName')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedLocs.map(loc => (
                                <TableRow key={loc.language.id}>
                                    <TableCell sx={{ ...sx14, width: 150 }}>{loc.language.name}</TableCell>
                                    <TableCell>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{loc.name}</Typography>
                                        ) : (
                                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                                value={loc.name ?? ''}
                                                onChange={e => updateLocalization(loc.language.id, e.target.value)} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
